import React, { type ComponentProps, type PropsWithChildren, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { Field } from '@atlaskit/form';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';
import TextField, { type TextFieldProps } from '@atlaskit/textfield';

import { ColorTile, type ColorTileProps } from '../../../../common/ui/color-tile';

type FieldProps = Omit<ComponentProps<typeof Field>, 'children' | 'label' | 'defaultValue'>;

export type ColorInputProps = PropsWithChildren<
	FieldProps &
		Pick<ColorTileProps, 'builtInColorMap' | 'value'> & {
			analyticsSource?: string;
			onChange(color?: string): void;
		}
>;

const colorTileContainerStyles = xcss({
	alignItems: 'center',
	paddingLeft: 'space.100',
});

export const ColorInput = ({
	analyticsSource,
	builtInColorMap,
	children,
	onChange,
	value,
	...fieldProps
}: ColorInputProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	value = value.toLowerCase();

	const onTextFieldChange = useCallback<NonNullable<TextFieldProps['onChange']>>(
		(event) => {
			onChange(event.currentTarget.value.toLowerCase());
		},
		[onChange],
	);

	const onTextFieldFocus = useCallback<NonNullable<TextFieldProps['onChange']>>(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'CustomColor',
				actionSubjectId: 'customPalette',
				source: analyticsSource,
			},
		}).fire();
	}, [analyticsSource, createAnalyticsEvent]);

	return (
		<TextField
			{...fieldProps}
			elemAfterInput={children}
			elemBeforeInput={
				<Flex xcss={colorTileContainerStyles}>
					<ColorTile builtInColorMap={builtInColorMap} value={value} />
				</Flex>
			}
			onChange={onTextFieldChange}
			onFocus={onTextFieldFocus}
			value={value.toUpperCase()}
		/>
	);
};
